/** @jsxImportSource @emotion/react */
import React from 'react'
import { css } from '@emotion/react'
import { Modal } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'

interface CancelConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  cancelling: boolean
}

/**
 * Confirmation dialog shown before cancelling schedule generation.
 */
const CancelConfirmModal: React.FC<CancelConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  cancelling,
}) => {
  return (
    <Modal
      css={css`
        .ant-modal-content {
          border-radius: 8px;
          background: ${colors.SECONDARY_300};
          border: 1px solid ${colors.SECONDARY_100};
          padding: 0;
          overflow: hidden;
        }
        .ant-modal-body {
          padding: 0;
        }
        .ant-modal-header {
          display: none;
        }
        .ant-modal-close {
          display: none;
        }
      `}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={420}
      centered
      destroyOnClose
      maskClosable={!cancelling}
      styles={{
        mask: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(6, 10, 16, 0.8)',
        },
      }}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          padding: 32px 24px 24px;
          text-align: center;
        `}
      >
        {/* Warning icon */}
        <div
          css={css`
            width: 48px;
            height: 48px;
            border-radius: 50%;
            background: ${colors.ERROR_300}20;
            display: flex;
            align-items: center;
            justify-content: center;
          `}
        >
          <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
            <path
              d='M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z'
              fill={colors.ERROR_300}
            />
          </svg>
        </div>

        <p
          css={css`
            font-family: 'Inter', sans-serif;
            font-weight: 600;
            font-size: 16px;
            color: ${colors.WHITE};
            margin: 0;
          `}
        >
          Cancel Schedule Generation?
        </p>
        <p
          css={css`
            font-family: 'Inter', sans-serif;
            font-weight: 400;
            font-size: 14px;
            color: ${colors.NEUTRAL_50};
            margin: 0;
            line-height: 1.5;
          `}
        >
          This will stop the scheduler and discard any in-progress results.
          Completed divisions will also be removed.
        </p>
      </div>

      {/* Actions */}
      <div
        css={css`
          display: flex;
          gap: 16px;
          padding: 16px 24px;
          border-top: 1px solid ${colors.SECONDARY_100};
        `}
      >
        <button
          type='button'
          onClick={onClose}
          disabled={cancelling}
          css={css`
            flex: 1;
            padding: 9px 24px;
            border-radius: 8px;
            border: 1px solid ${colors.WHITE};
            background: transparent;
            color: ${colors.WHITE};
            font-family: ${isCanlan
              ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
              : 'BarlowCondensed, Arial, Helvetica, sans-serif'};
            font-size: 18px;
            font-weight: 600;
            cursor: pointer;
            text-transform: uppercase;
            transition: all 0.2s ease;

            &:hover {
              background: rgba(255, 255, 255, 0.1);
            }
            &:disabled {
              opacity: 0.5;
              cursor: not-allowed;
            }
          `}
        >
          GO BACK
        </button>
        <button
          type='button'
          onClick={onConfirm}
          disabled={cancelling}
          css={css`
            flex: 1;
            padding: 9px 24px;
            border-radius: 8px;
            border: none;
            background: ${colors.ERROR_300};
            color: ${colors.WHITE};
            font-family: ${isCanlan
              ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
              : 'BarlowCondensed, Arial, Helvetica, sans-serif'};
            font-size: 18px;
            font-weight: 600;
            cursor: pointer;
            text-transform: uppercase;
            transition: all 0.2s ease;

            &:hover {
              background: ${colors.ERROR_400};
            }
            &:disabled {
              opacity: 0.6;
              cursor: not-allowed;
            }
          `}
        >
          {cancelling ? 'CANCELLING...' : 'CANCEL GENERATION'}
        </button>
      </div>
    </Modal>
  )
}

export default CancelConfirmModal
